import React, {
  useMemo,
  useRef,
  useState,
} from "react";

import PropTypes from "prop-types";


const RESTOCK_OPTIONS =
  Object.freeze([
    {
      value:
        "restock",

      label:
        "Réintégrer au stock",
    },
    {
      value:
        "damaged",

      label:
        "Article endommagé",
    },
    {
      value:
        "discard",

      label:
        "Écarter / détruire",
    },
    {
      value:
        "no_restock",

      label:
        "Ne pas réintégrer",
    },
  ]);


const REFUND_PAYMENT_METHODS =
  Object.freeze([
    {
      value:
        "card",

      label:
        "Carte",
    },
    {
      value:
        "cash",

      label:
        "Espèces",
    },
    {
      value:
        "check",

      label:
        "Chèque",
    },
    {
      value:
        "transfer",

      label:
        "Virement",
    },
    {
      value:
        "gift_cards",

      label:
        "Carte cadeau",
    },
  ]);


export default function RetailPOSReturnRefundWorkspace({
  runtime,
  context,
  receiptSnapshot,
}) {
  const receiptUuid =
    resolveReceiptUuid(
      receiptSnapshot
    );

  const salesLines =
    useMemo(
      () =>
        resolveSalesLines(
          receiptSnapshot
        ),
      [
        receiptSnapshot,
      ]
    );


  const [
    selectedLineUuid,
    setSelectedLineUuid,
  ] = useState("");

  const [
    returnQuantity,
    setReturnQuantity,
  ] = useState("1");

  const [
    restockDisposition,
    setRestockDisposition,
  ] = useState("restock");

  const [
    returnReason,
    setReturnReason,
  ] = useState("");

  const [
    returnPending,
    setReturnPending,
  ] = useState(false);

  const [
    returnError,
    setReturnError,
  ] = useState(null);

  const [
    returnSnapshot,
    setReturnSnapshot,
  ] = useState(null);


  const [
    refundAmount,
    setRefundAmount,
  ] = useState("");

  const [
    refundMeansPayment,
    setRefundMeansPayment,
  ] = useState("card");

  const [
    refundReason,
    setRefundReason,
  ] = useState("");

  const [
    refundPending,
    setRefundPending,
  ] = useState(false);

  const [
    refundError,
    setRefundError,
  ] = useState(null);

  const [
    refundSnapshot,
    setRefundSnapshot,
  ] = useState(null);


  const returnInFlightRef =
    useRef(false);

  const refundInFlightRef =
    useRef(false);

  const returnIdempotencyRef =
    useRef(
      createIdempotencyKey(
        "return"
      )
    );

  const refundIdempotencyRef =
    useRef(
      createIdempotencyKey(
        "refund"
      )
    );


  async function handleReturnSubmit(
    event
  ) {
    event.preventDefault();

    if (
      returnInFlightRef.current
      || !receiptUuid
    ) {
      return;
    }

    if (!selectedLineUuid) {
      setReturnError(
        "Sélectionnez une ligne à retourner."
      );

      return;
    }

    if (
      returnQuantity === ""
      || Number(returnQuantity) <= 0
    ) {
      setReturnError(
        "La quantité doit être strictement positive."
      );

      return;
    }

    returnInFlightRef.current =
      true;

    setReturnPending(true);
    setReturnError(null);

    try {
      const result =
        await runtime.returnProduct(
          {
            receiptUuid,

            idempotencyKey:
              returnIdempotencyRef
                .current,

            reason:
              normalizeOptionalText(
                returnReason
              ),

            lines: [
              {
                salesLineUuid:
                  selectedLineUuid,

                quantity:
                  returnQuantity,

                restockDisposition,

                reason:
                  normalizeOptionalText(
                    returnReason
                  ),
              },
            ],
          },
          context
        );

      setReturnSnapshot(
        result?.return
        ?? null
      );

      returnIdempotencyRef.current =
        createIdempotencyKey(
          "return"
        );
    } catch (error) {
      setReturnError(
        error?.message
        ?? "Impossible d'enregistrer le retour."
      );
    } finally {
      returnInFlightRef.current =
        false;

      setReturnPending(false);
    }
  }


  async function handleRefundSubmit(
    event
  ) {
    event.preventDefault();

    if (
      refundInFlightRef.current
      || !receiptUuid
    ) {
      return;
    }

    if (
      refundAmount === ""
      || Number(refundAmount) <= 0
    ) {
      setRefundError(
        "Le montant doit être strictement positif."
      );

      return;
    }

    refundInFlightRef.current =
      true;

    setRefundPending(true);
    setRefundError(null);

    try {
      const result =
        await runtime.refund(
          {
            receiptUuid,

            idempotencyKey:
              refundIdempotencyRef
                .current,

            amount:
              refundAmount,

            meansPayment:
              refundMeansPayment,

            salesReturnUuid:
              returnSnapshot?.uuid
              ?? null,

            reason:
              normalizeOptionalText(
                refundReason
              ),
          },
          context
        );

      setRefundSnapshot(
        result?.refund
        ?? null
      );

      refundIdempotencyRef.current =
        createIdempotencyKey(
          "refund"
        );
    } catch (error) {
      setRefundError(
        error?.message
        ?? "Impossible d'enregistrer le remboursement."
      );
    } finally {
      refundInFlightRef.current =
        false;

      setRefundPending(false);
    }
  }


  if (!receiptUuid) {
    return (
      <section
        className="retail-pos-return-refund"
        data-testid="retail-pos-return-refund-workspace"
        aria-label="Retours et remboursements"
      >
        <p
          role="status"
          data-testid="retail-pos-return-refund-unavailable"
        >
          Receipt autoritaire indisponible.
        </p>
      </section>
    );
  }


  return (
    <section
      className="retail-pos-return-refund"
      data-testid="retail-pos-return-refund-workspace"
      aria-labelledby="retail-pos-return-refund-title"
    >
      <header
        className="retail-pos-return-refund__header"
      >
        <div>
          <p
            className="retail-pos-return-refund__eyebrow"
          >
            Après-vente
          </p>

          <h3
            id="retail-pos-return-refund-title"
          >
            Retours & remboursements
          </h3>
        </div>

        <span>
          Receipt {receiptUuid}
        </span>
      </header>


      <div
        className="retail-pos-return-refund__grid"
      >
        <form
          className="retail-pos-return-refund__section"
          onSubmit={
            handleReturnSubmit
          }
          data-testid="retail-pos-return-form"
        >
          <h4>
            Retour produit
          </h4>

          {salesLines.length === 0
            ? (
              <p
                role="status"
                data-testid="retail-pos-return-lines-unavailable"
              >
                Aucune ligne autoritaire disponible pour le retour.
              </p>
            )
            : (
              <>
                <label>
                  Ligne de vente

                  <select
                    value={
                      selectedLineUuid
                    }
                    onChange={
                      (event) =>
                        setSelectedLineUuid(
                          event.target.value
                        )
                    }
                    data-testid="retail-pos-return-line"
                  >
                    <option
                      value=""
                    >
                      Sélectionner…
                    </option>

                    {salesLines.map(
                      (line) => (
                        <option
                          key={
                            line.uuid
                          }
                          value={
                            line.uuid
                          }
                        >
                          {resolveLineLabel(
                            line
                          )}
                        </option>
                      )
                    )}
                  </select>
                </label>


                <label>
                  Quantité

                  <input
                    type="number"
                    min="0.001"
                    step="0.001"
                    value={
                      returnQuantity
                    }
                    onChange={
                      (event) =>
                        setReturnQuantity(
                          event.target.value
                        )
                    }
                    data-testid="retail-pos-return-quantity"
                  />
                </label>


                <label>
                  Traitement stock

                  <select
                    value={
                      restockDisposition
                    }
                    onChange={
                      (event) =>
                        setRestockDisposition(
                          event.target.value
                        )
                    }
                    data-testid="retail-pos-return-disposition"
                  >
                    {RESTOCK_OPTIONS.map(
                      (option) => (
                        <option
                          key={
                            option.value
                          }
                          value={
                            option.value
                          }
                        >
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </label>


                <label>
                  Motif

                  <textarea
                    value={
                      returnReason
                    }
                    onChange={
                      (event) =>
                        setReturnReason(
                          event.target.value
                        )
                    }
                    data-testid="retail-pos-return-reason"
                  />
                </label>


                <button
                  type="submit"
                  disabled={
                    returnPending
                  }
                  data-testid="retail-pos-return-submit"
                >
                  {returnPending
                    ? "Enregistrement…"
                    : "Enregistrer le retour"}
                </button>
              </>
            )}


          {returnError && (
            <p
              role="alert"
              data-testid="retail-pos-return-error"
            >
              {returnError}
            </p>
          )}


          {returnSnapshot && (
            <div
              className="retail-pos-return-refund__result"
              data-testid="retail-pos-return-result"
            >
              <strong>
                Retour enregistré
              </strong>

              <span>
                {returnSnapshot.uuid}
              </span>

              <span>
                Statut : {
                  returnSnapshot.status
                  ?? "pending"
                }
              </span>
            </div>
          )}
        </form>


        <form
          className="retail-pos-return-refund__section"
          onSubmit={
            handleRefundSubmit
          }
          data-testid="retail-pos-refund-form"
        >
          <h4>
            Remboursement
          </h4>

          <label>
            Montant

            <input
              type="number"
              min="0.01"
              step="0.01"
              value={
                refundAmount
              }
              onChange={
                (event) =>
                  setRefundAmount(
                    event.target.value
                  )
              }
              data-testid="retail-pos-refund-amount"
            />
          </label>


          <label>
            Moyen de remboursement

            <select
              value={
                refundMeansPayment
              }
              onChange={
                (event) =>
                  setRefundMeansPayment(
                    event.target.value
                  )
              }
              data-testid="retail-pos-refund-means"
            >
              {REFUND_PAYMENT_METHODS.map(
                (option) => (
                  <option
                    key={
                      option.value
                    }
                    value={
                      option.value
                    }
                  >
                    {option.label}
                  </option>
                )
              )}
            </select>
          </label>


          {returnSnapshot?.uuid && (
            <p
              className="retail-pos-return-refund__link"
              data-testid="retail-pos-refund-return-link"
            >
              Lié au retour : {
                returnSnapshot.uuid
              }
            </p>
          )}


          <label>
            Motif

            <textarea
              value={
                refundReason
              }
              onChange={
                (event) =>
                  setRefundReason(
                    event.target.value
                  )
              }
              data-testid="retail-pos-refund-reason"
            />
          </label>


          <button
            type="submit"
            disabled={
              refundPending
            }
            data-testid="retail-pos-refund-submit"
          >
            {refundPending
              ? "Enregistrement…"
              : "Enregistrer le remboursement"}
          </button>


          {refundError && (
            <p
              role="alert"
              data-testid="retail-pos-refund-error"
            >
              {refundError}
            </p>
          )}


          {refundSnapshot && (
            <div
              className="retail-pos-return-refund__result"
              data-testid="retail-pos-refund-result"
            >
              <strong>
                Remboursement enregistré
              </strong>

              <span>
                {refundSnapshot.uuid}
              </span>

              <span>
                Statut : {
                  refundSnapshot.status
                  ?? "pending"
                }
              </span>
            </div>
          )}
        </form>
      </div>


      <p
        className="retail-pos-return-refund__boundary"
      >
        Les quantités et montants autorisés sont validés
        par le backend transactionnel.
      </p>
    </section>
  );
}


function resolveReceiptUuid(
  snapshot
) {
  const candidate =
    snapshot?.receipt?.uuid
    ?? snapshot?.receiptUuid
    ?? snapshot?.receipt_uuid
    ?? snapshot?.uuid;

  if (
    typeof candidate !== "string"
    || candidate.trim() === ""
  ) {
    return null;
  }

  return candidate.trim();
}


function resolveSalesLines(
  snapshot
) {
  if (
    !Array.isArray(
      snapshot?.lines
    )
  ) {
    return [];
  }

  return snapshot.lines.filter(
    (line) =>
      line
      && typeof line === "object"
      && typeof line.uuid === "string"
      && line.uuid.trim() !== ""
  );
}


function resolveLineLabel(
  line
) {
  const label =
    line.wording
    ?? line.name
    ?? line.label
    ?? line.uuid;

  const quantity =
    line.quantity
    ?? "?";

  return `${label} — Qté ${quantity}`;
}


function normalizeOptionalText(
  value
) {
  if (
    typeof value !== "string"
    || value.trim() === ""
  ) {
    return null;
  }

  return value.trim();
}


function createIdempotencyKey(
  prefix
) {
  if (
    typeof globalThis.crypto
      ?.randomUUID === "function"
  ) {
    return `${prefix}-${globalThis.crypto.randomUUID()}`;
  }

  return (
    `${prefix}-${Date.now()}-`
    + Math.random()
      .toString(36)
      .slice(2)
  );
}


RetailPOSReturnRefundWorkspace.propTypes = {
  runtime:
    PropTypes.shape({
      returnProduct:
        PropTypes.func.isRequired,

      refund:
        PropTypes.func.isRequired,
    }).isRequired,

  context:
    PropTypes.shape({
      companyUuid:
        PropTypes.string,

      activityUuid:
        PropTypes.string,
    }).isRequired,

  receiptSnapshot:
    PropTypes.object,
};
